<template>
    <div class="max-w-4xl mx-auto p-4 flex flex-col gap-6">
        <h1 class="text-2xl font-semibold text-theme-base">Mi plan</h1>

        <custom-navbar
            v-if="canListInvoices"
            id="tabs-plan"
            v-model="activeTab"
            center-collapsed
            not-line
        >
            <template #left>
                <custom-navbar-item
                    id="plan"
                    :active="activeTab === 'plan'"
                    class="px-2 md:px-4 xl:px-6"
                >
                    <div class="flex">
                        <span class="lg:block hidden">Mi plan</span>
                        <span>&nbsp;⭐</span>
                    </div>
                </custom-navbar-item>
                <custom-navbar-item
                    id="invoices"
                    :active="activeTab === 'invoices'"
                    class="px-2 md:px-4 xl:px-6"
                >
                    <div class="flex">
                        <span class="lg:block hidden">Mis facturas</span>
                        <span>&nbsp;🧾</span>
                    </div>
                </custom-navbar-item>
            </template>
        </custom-navbar>

        <p
            v-if="activeTab === 'plan' && loaded && !current"
            class="text-xl font-semibold text-theme-base"
        >
            Todavía no tienes un plan.
        </p>

        <template v-if="activeTab === 'plan' && current">
            <platform-subscription-card
                :current="current"
                :scheduled="scheduled"
                :pending-change="pendingChange"
            >
                <p
                    v-if="
                        current.status === 'past_due' && current.gracePeriodEnd
                    "
                    class="rounded-xl bg-theme-warning px-4 py-3 text-sm text-black"
                >
                    Tienes hasta el
                    {{ current.gracePeriodEnd | dateSimple }} para pagar. Si no
                    pagas, tu plan pasa a Free.
                </p>

                <p
                    v-if="trialEndingSoon"
                    class="rounded-xl bg-theme-warning px-4 py-3 text-sm text-black"
                >
                    Tu prueba gratis termina pronto. Elige un plan para no pasar
                    a Free.
                </p>

                <div
                    v-if="pendingChange"
                    class="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-theme-base"
                >
                    <span>
                        A partir del
                        {{ pendingChange.startsAt | dateSimple }} pasas a
                        <strong>{{ pendingChange.planName }}</strong
                        >.
                    </span>
                    <button
                        class="text-theme-primary underline"
                        :disabled="isUndoing"
                        @click="keepCurrentPlan"
                    >
                        Mantener mi plan actual
                    </button>
                </div>

                <div
                    v-if="actions.changePlan"
                    class="flex flex-wrap gap-4 border-t border-theme-border pt-3 text-sm"
                >
                    <router-link
                        to="/app/plan/change"
                        class="text-theme-primary underline"
                    >
                        Cambiar de plan
                    </router-link>
                    <button
                        v-if="current.type !== 'free'"
                        class="text-theme-muted underline"
                        @click="openCancelPlan"
                    >
                        Cancelar plan
                    </button>
                </div>
            </platform-subscription-card>

            <div
                v-if="actions.pay && payableInvoice"
                class="rounded-2xl border border-theme-border bg-theme-card p-5 flex flex-wrap items-center justify-between gap-4"
            >
                <div class="flex flex-col gap-1">
                    <p
                        class="text-xs font-semibold uppercase tracking-wide text-theme-muted"
                    >
                        Factura por pagar
                    </p>
                    <p class="text-2xl font-bold text-theme-base">
                        {{ payableInvoice.amount | currency }}
                    </p>
                    <p class="text-sm text-theme-muted">
                        Vence el {{ payableInvoice.dueDate | date }}
                    </p>
                </div>
                <app-button
                    color="brand-blue"
                    :loading="isPaying"
                    @click="payInvoice"
                >
                    Pagar
                </app-button>
                <legal-payment-notice class="w-full" />
            </div>

            <div v-if="usageItems.length" class="flex flex-col gap-3">
                <h2 class="text-lg font-semibold text-theme-base">Tu uso</h2>
                <plan-usage-list :items="usageItems" @upgrade="goToPlan" />
            </div>
        </template>

        <div
            v-if="activeTab === 'invoices' && invoicesLoaded"
            class="flex flex-col gap-2"
        >
            <platform-invoices-table :items="invoices" />
        </div>

        <plan-change-confirm-dialog
            v-model="showCancelDialog"
            :preview="cancelPreview"
            is-free
            :loading="isCanceling"
            @confirm="confirmCancelPlan"
        />
    </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";

import { findPlanResource } from "@/common/fields/plan-features";
import {
    buildPaymentOpeningRoute,
    savePaymentLink
} from "@/common/lib/payment-link";

import LegalPaymentNotice from "@/components/legal/LegalPaymentNotice.vue";
import PlanUsageList from "@/components/utils/PlanUsageList.vue";
import PlatformInvoicesTable from "@/components/utils/PlatformInvoicesTable.vue";
import PlatformSubscriptionCard from "@/components/utils/PlatformSubscriptionCard.vue";

import PlanChangeConfirmDialog from "./PlanChangeConfirmDialog.vue";

const FREE_PLAN_CODE = "free";
const TRIAL_WARNING_DAYS = 5;
const DAY_MS = 24 * 60 * 60 * 1000;

export default {
    name: "PlanIndex",
    components: {
        LegalPaymentNotice,
        PlanUsageList,
        PlatformInvoicesTable,
        PlatformSubscriptionCard,
        PlanChangeConfirmDialog
    },
    data: () => ({
        activeTab: "plan",
        loaded: false,
        state: null,
        invoices: [],
        invoicesLoaded: false,
        isPaying: false,
        isUndoing: false,
        showCancelDialog: false,
        cancelPreview: null,
        freePlanSlug: null,
        isCanceling: false
    }),
    computed: {
        ...mapGetters("control", ["backgroundColor"]),
        ...mapGetters("platformSubscriptions", ["upgradePlanForResource"]),
        ...mapState("auth", ["subscription"]),
        usageItems() {
            const usage = (this.state && this.state.usage) || [];
            return usage
                .filter((item) => findPlanResource(item.resourceCode))
                .map((item) => ({
                    ...item,
                    label: findPlanResource(item.resourceCode).label,
                    upgradePlan: this.upgradePlanForResource(
                        item.resourceCode,
                        item.used
                    )
                }));
        },
        canListInvoices() {
            return this.$ability.hasPermission(
                "invoice:ListPlatformInvoicesMine"
            );
        },
        current() {
            return this.state ? this.state.current : null;
        },
        scheduled() {
            return this.state ? this.state.scheduled : null;
        },
        pendingChange() {
            return this.state ? this.state.pendingChange : null;
        },
        payableInvoice() {
            return this.state ? this.state.payableInvoice : null;
        },
        actions() {
            return this.state
                ? this.state.actions
                : { pay: false, changePlan: false };
        },
        trialEndingSoon() {
            if (!this.current || this.current.type !== "trial") return false;
            const remaining =
                new Date(this.current.currentPeriodEnd).getTime() - Date.now();
            return remaining <= TRIAL_WARNING_DAYS * DAY_MS;
        }
    },
    watch: {
        // Las facturas se piden solo si el cliente abre su tab.
        activeTab(tab) {
            if (tab === "invoices" && !this.invoicesLoaded) {
                this.loadInvoices();
            }
        }
    },
    async mounted() {
        const verifyPagePermission =
            this.$ability.verifyPagePermission.bind(this);
        if (
            !(await verifyPagePermission(
                "platformSubscription:GetMine",
                "/app"
            ))
        )
            return;
        await this.loadSubscription();
    },
    methods: {
        ...mapActions("platformSubscriptions", [
            "getMySubscription",
            "previewMyPlanChange",
            "changeMyPlan",
            "payPlatformInvoice",
            "listActivePlatformPlans",
            "listMyPlatformInvoices",
            "ensurePlanCatalog"
        ]),
        ...mapActions("auth", ["getProfile"]),
        async showError(error) {
            this.$sound.play("error");
            await this.$swal.fire({
                background: this.backgroundColor,
                title: error.title,
                text: error.message,
                icon: error.icon
            });
        },
        goToPlan(plan) {
            this.$router.push(`/app/plan/change?plan=${plan.code}`);
        },
        async loadSubscription() {
            this.$loading.show();
            try {
                const [state] = await Promise.all([
                    this.getMySubscription(),
                    this.ensurePlanCatalog()
                ]);
                this.state = state;
                this.loaded = true;
                this.$loading.hide();
            } catch (error) {
                this.$loading.hide();
                await this.showError(error);
                return;
            }
            await this.refreshSessionIfPlanChanged();
        },
        // El plan pudo cambiar por fuera de la sesión (fin del trial, pago
        // confirmado): solo entonces se recarga el perfil, para que menú,
        // candados y tarjetas reflejen el plan vigente.
        async refreshSessionIfPlanChanged() {
            const current = this.current;
            const session = this.subscription;
            const isSame =
                current && session
                    ? current.type === session.type &&
                      current.planSlug.startsWith(`${session.planCode}_`)
                    : !current && !session;
            if (isSame) return;
            try {
                await this.getProfile();
            } catch {
                // Se reintenta en la siguiente visita
            }
        },
        async loadInvoices() {
            try {
                const { items } = await this.$loading.wrap(() =>
                    this.listMyPlatformInvoices()
                );
                this.invoices = items;
                this.invoicesLoaded = true;
            } catch (error) {
                await this.showError(error);
            }
        },
        async payInvoice() {
            this.isPaying = true;
            try {
                const { invoiceId, paymentUrl } = await this.payPlatformInvoice(
                    {
                        id: this.payableInvoice.id
                    }
                );
                savePaymentLink(invoiceId, paymentUrl);
                this.$router.push(
                    buildPaymentOpeningRoute("my_plan", invoiceId)
                );
            } catch (error) {
                this.isPaying = false;
                await this.showError(error);
            }
        },
        // Deshacer: elegir de nuevo el plan vigente anula el cambio pendiente.
        async keepCurrentPlan() {
            this.isUndoing = true;
            try {
                await this.changeMyPlan({ planSlug: this.current.planSlug });
                this.isUndoing = false;
                this.$toast.success("Mantienes tu plan actual");
                await this.loadSubscription();
            } catch (error) {
                this.isUndoing = false;
                await this.showError(error);
            }
        },
        // Cancelar = bajar a Free en el corte.
        async openCancelPlan() {
            try {
                const plans = await this.$loading.wrap(() =>
                    this.listActivePlatformPlans()
                );
                const freePlan = plans.items.find(
                    (plan) => plan.code === FREE_PLAN_CODE
                );
                this.freePlanSlug = freePlan ? freePlan.slug : null;
                if (!this.freePlanSlug) return;
                this.cancelPreview = await this.$loading.wrap(() =>
                    this.previewMyPlanChange({ planSlug: this.freePlanSlug })
                );
                this.showCancelDialog = true;
            } catch (error) {
                await this.showError(error);
            }
        },
        async confirmCancelPlan() {
            this.isCanceling = true;
            try {
                await this.changeMyPlan({ planSlug: this.freePlanSlug });
                this.isCanceling = false;
                this.showCancelDialog = false;
                this.$toast.success(
                    "Tu plan pasará a Free en la fecha de corte"
                );
                await this.loadSubscription();
            } catch (error) {
                this.isCanceling = false;
                await this.showError(error);
            }
        }
    }
};
</script>
